import { useState, useEffect } from "react";
import { convertDecimalToFraction } from "../util/formulas/convertDecimalToFraction";
import calcSwingWidths from "../util/formulas/calculateSwingWidths";
import { UnitObj } from "../util/quoteCartStorage";
import { sidelite_dlo } from "../components/SwingConfiguration/Sidelites/Sidelites";
import fractionToInt from "../util/fractionToInt";

interface UpdateObj {
  swingPanelWidth: string;
  swingPanelFraction: string;
  sideLiteWidth?: string;
  sideLiteDLO?: string;
  dropseal?: string;
}

const useSwingWidth = (
  width: string,
  widthFraction: string,
  direction: string,
  swingType: string,
  sideLite: string,
  pWidth: string,
  pWidthFraction: string,
  addToQuote: (
    updateObj: Partial<UnitObj>,
    ignoreKey?: string | undefined,
  ) => void,
  handleErrMsgChange: (errKey: string, err: string) => void,
  editBtn: boolean,
  equalPanels: boolean,
  material: string,
  isPivot: boolean,
  screenType: string,
  alumThickFrame: boolean,
  isPico: boolean,
  exactConfig: string,
  stilesAndRailsCategory: string,
) => {
  const [panelWidth, setPanelWidth] = useState(pWidth);
  const [panelFraction, setPanelFraction] = useState(pWidthFraction);
  const [sideliteWidth, setSideliteWidth] = useState("");

  const [equalP, setEqualP] = useState("");

  const isAlum = material?.includes("Aluminum");

  const findSwingWidths = () => {
    const { panelW, slw, equalized } = calcSwingWidths({
      width,
      widthFraction,
      direction,
      swingType,
      sideLite,
      pWidth,
      pWidthFraction,
      equalPanels,
      material,
      isPivot,
      screenType,
      thickAlum: alumThickFrame,
      isPico,
      exactConfig: exactConfig,
    });
    if (equalized) {
      const fraction = convertDecimalToFraction(equalized);

      setEqualP(fraction);

      const sidelitedlo = sidelite_dlo(fraction, material, direction, isPico);

      let update: UpdateObj = {
        swingPanelWidth: fraction.split(" ")[0],
        swingPanelFraction: fraction.split(" ")[1] || "",
        sideLiteWidth: fraction,
        sideLiteDLO: sidelitedlo,
      };

      if (isPivot && equalized > 90) {
        update["dropseal"] = "";
      }

      addToQuote(update);

      let min = 24;
      let max = 42;

      if (isAlum || isPico) {
        min = 24;
        max = 48;
      } else if (isPivot) {
        min = 28;
        max = 60;
      }

      const roundedEqualizedValue = fractionToInt(
        fraction.split(" ")[0],
        fraction.split(" ")[1] || "",
      );

      const errText =
        roundedEqualizedValue > max || roundedEqualizedValue < min
          ? `Panel/Sidelite width must be between ${min} and ${max} inches`
          : "";
      handleErrMsgChange("sideliteWidthErr", errText);
    } else {
      if (panelW && sideLite === "none") {
        const sepByDecimal = panelW.toString().split(".");
        const panelDecimal = convertDecimalToFraction(
          parseFloat(`.${sepByDecimal[1]}`),
        );
        const wholeNumber = sepByDecimal[0];

        setPanelWidth(wholeNumber);
        setPanelFraction(panelDecimal.split(" ")[1]);

        let update: UpdateObj = {
          swingPanelWidth: wholeNumber,
          swingPanelFraction: panelDecimal.split(" ")[1],
        };

        if (isPivot && panelW > 90) {
          update["dropseal"] = "";
        }

        addToQuote(update);
      } else if (sideLite) {
        const converted = convertDecimalToFraction(slw);
        setSideliteWidth(converted);

        const sidelitedlo = sidelite_dlo(
          converted,
          material,
          direction,
          isPico,
        );

        addToQuote({
          sideLiteWidth: converted,
          sideLiteDLO: sidelitedlo,
        });

        let min = 11;
        let max = 43;

        if (isAlum || isPico) {
          max = 48;
        } else if (isPivot) {
          max = 60;
        }

        const roundedSideliteWidthValue = fractionToInt(
          converted.split(" ")[0],
          converted.split(" ")[1] || "",
        );

        const errText =
          roundedSideliteWidthValue > max || roundedSideliteWidthValue < min
            ? `Sidelite width must be between ${min} and ${max} inches`
            : "";

        handleErrMsgChange("sideliteWidthErr", errText);
      }
    }
  };

  useEffect(() => {
    if (!direction) return;

    if (
      !width ||
      !swingType ||
      width.length < 2 ||
      (!equalPanels && sideLite !== "none" && pWidth.length < 2)
    ) {
      setPanelWidth("");
      setPanelFraction("");
      setSideliteWidth("");
      setEqualP("");
      return;
    }

    findSwingWidths();
  }, [
    width,
    widthFraction,
    direction,
    swingType,
    sideLite,
    pWidth,
    pWidthFraction,
    editBtn,
    equalPanels,
    material,
    isPivot,
    screenType,
    alumThickFrame,
    stilesAndRailsCategory,
  ]);

  return { panelWidth, panelFraction, sideliteWidth, equalP };
};

export default useSwingWidth;
